import { Avatar } from "@dash/avatar";
import { useDeviceType } from "@dash/mq";
import { DocsSelectDialog } from "@dash/select";
import { useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import React, { useContext, useEffect, useState } from "react";
import { MdOutlineGroupAdd } from "react-icons/md";
import { useQueryClient } from "react-query";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { ResourceId } from "@megaron/resource";
import { Priority, TicketDetailDto } from "@megaron/support-contracts";

import { CrmCustomerContacts } from "../crm/CrmCustomerContacts";
import { useResolvedCrmCustomerId } from "../crm/useResolvedCrmCustomerId";
import { SupportTicketInvoiceNumberField } from "../invoice/SupportTicketInvoiceNumberField";
import { SupportPrioritySelect } from "../shared/SupportPrioritySelect";
import { SupportStatusSelect } from "../shared/SupportStatusSelect";
import { SupportTicketCategorySelect } from "../shared/SupportTicketCategorySelect";
import { useSupportTicketEditable } from "../shared/useSupportTicketEditable";
import { SupportTicketMetaAssigneesSection } from "./SupportTicketMetaAssigneesSection";
import { SupportTicketRelatedTickets } from "./SupportTicketRelatedTickets";
import { updateTicketPayload } from "./supportUpdateTicketPayload";

type Props = {
  ticket: TicketDetailDto;
  listPath?: string;
};

export const SupportTicketMetaSection: React.FC<Props> = ({ ticket, listPath = "/support/tickets" }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const auth = useContext(IamAuthContext);
  const support = useClientManager("support");
  const resolvedCustomerId = useResolvedCrmCustomerId(ticket);
  const updateMutation = support.updateTicket().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const updateAssigneesMutation = support.updateTicketAssignees().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const { isDesktop } = useDeviceType();
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState(false);
  const [invoiceNumberDraft, setInvoiceNumberDraft] = useState(ticket.invoiceNumber ?? "");
  const [orderNumberDraft, setOrderNumberDraft] = useState(ticket.orderNumber ?? "");

  useEffect(() => {
    setInvoiceNumberDraft(ticket.invoiceNumber ?? "");
  }, [ticket.invoiceNumber]);

  useEffect(() => {
    setOrderNumberDraft(ticket.orderNumber ?? "");
  }, [ticket.orderNumber]);

  const { profile } = useProfiles();
  const isBusy = "isLoading" in updateMutation ? updateMutation.isLoading : false;
  const isAssigneesBusy = "isLoading" in updateAssigneesMutation ? updateAssigneesMutation.isLoading : false;
  const userEmail =
    auth.iamUser?.userType === "megarax" ? auth.iamUser.email ?? undefined : undefined;
  const assigneeEmails = ticket.assignees?.map((a) => a.email) ?? [];
  const isAssignee = !!userEmail && assigneeEmails.includes(userEmail);
  const isEditable = useSupportTicketEditable(ticket.status);

  const handleStatusChange = (status: TicketDetailDto["status"]) => {
    updateMutation.mutate(updateTicketPayload(ticket, { status }));
  };
  const handlePriorityChange = (priority: Priority | null) => {
    updateMutation.mutate(updateTicketPayload(ticket, { priority }));
  };
  const handleCategoryChange = (category: TicketDetailDto["category"] | null) => {
    updateMutation.mutate(updateTicketPayload(ticket, { category }));
  };
  const commitInvoiceNumber = () => {
    const next = invoiceNumberDraft.trim() || null;
    if (next === (ticket.invoiceNumber ?? null)) return;
    updateMutation.mutate(updateTicketPayload(ticket, { invoiceNumber: next }));
  };
  const commitOrderNumber = () => {
    const next = orderNumberDraft.trim() || null;
    if (next === (ticket.orderNumber ?? null)) return;
    updateMutation.mutate(updateTicketPayload(ticket, { orderNumber: next }));
  };
  const setAssignees = (emails: string[]) => {
    updateAssigneesMutation.mutate({ id: ticket.id, assigneeEmails: emails });
  };
  const removeAssignee = (email: string) => {
    const next = assigneeEmails.filter((e) => e !== email);
    updateAssigneesMutation.mutate({ id: ticket.id, assigneeEmails: next });
  };
  const isFormDisabled = !!isBusy || !!isAssigneesBusy || !isEditable;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {!isDesktop && (
        <div css={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
          <div css={{ display: "flex", alignItems: "center", gap: "0.25rem", minWidth: 0 }}>
            {ticket.assignees?.slice(0, 4).map((assignee) => {
              const userProfile = profile(assignee.email);
              return <Avatar key={assignee.email} src={userProfile?.profilePictureUrl ?? null} />;
            })}
            <button
              onClick={() => isEditable && setAssigneeDialogOpen(true)}
              disabled={!isEditable}
              css={{
                cursor: isEditable ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "100%",
                border: "none",
                padding: "0.25rem",
                width: "30px",
                height: "30px",
                background: theme.colors.primaryLight,
                flexShrink: 0,
                opacity: isEditable ? 1 : 0.5,
              }}
            >
              <MdOutlineGroupAdd size={16} css={{ color: theme.colors.primary }} />
            </button>
          </div>
          <div css={{ minWidth: "9rem" }}>
            <SupportStatusSelect value={ticket.status} onChange={handleStatusChange} isDisabled={isFormDisabled} />
          </div>
        </div>
      )}
      {isDesktop && (
        <SupportStatusSelect value={ticket.status} onChange={handleStatusChange} isDisabled={isFormDisabled} />
      )}
      <SupportPrioritySelect value={ticket.priority} onChange={handlePriorityChange} isDisabled={isFormDisabled} />
      <SupportTicketCategorySelect
        value={ticket.category}
        onChange={handleCategoryChange}
        isDisabled={isFormDisabled}
      />
      {isDesktop && (
        <SupportTicketMetaAssigneesSection
          ticket={ticket}
          isEditable={isEditable}
          isAssigneesBusy={!!isAssigneesBusy}
          userEmail={userEmail}
          isAssignee={isAssignee}
          onJoin={() => userEmail && setAssignees([...new Set([...assigneeEmails, userEmail])])}
          onRemoveAssignee={removeAssignee}
          onOpenAssigneeDialog={() => setAssigneeDialogOpen(true)}
        />
      )}
      <SupportTicketInvoiceNumberField
        invoiceNumber={invoiceNumberDraft}
        orderNumber={orderNumberDraft}
        onInvoiceNumberChange={setInvoiceNumberDraft}
        onOrderNumberChange={setOrderNumberDraft}
        onInvoiceBlur={commitInvoiceNumber}
        onOrderBlur={commitOrderNumber}
        isDisabled={isFormDisabled}
      />
      <DocsSelectDialog
        docType={["user"]}
        variant="multi-select"
        label="Przypisz użytkowników"
        initiallySelectedDocIds={ticket.assignees?.map((a) => ResourceId("user", a.email)) ?? []}
        noTrigger
        isOpen={assigneeDialogOpen}
        onClose={() => setAssigneeDialogOpen(false)}
        onSelectedChange={(chosen) => {
          const emails = chosen
            .map((o) => o?.value)
            .filter((email): email is string => !!email);
          setAssignees(emails);
          setAssigneeDialogOpen(false);
        }}
      />
      {isDesktop && <SupportTicketRelatedTickets ticket={ticket} listPath={listPath} />}
      {isDesktop && resolvedCustomerId && <CrmCustomerContacts customerId={resolvedCustomerId} />}
    </div>
  );
};
